import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  MessageSquare,
  Users,
  Clock,
  TrendingUp,
  Activity,
  CheckCircle,
  AlertCircle,
  UserCheck
} from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import { useChatStore } from '../stores/chatStore'
import { format } from 'date-fns'

const Dashboard = () => {
  const navigate = useNavigate()
  const { agent } = useAuthStore()
  const { sessions, fetchSessions } = useChatStore()

  useEffect(() => {
    fetchSessions()
  }, [fetchSessions])

  // Calculate statistics
  const stats = {
    totalChats: sessions.length,
    activeChats: sessions.filter(s => s.status === 'active').length,
    waitingChats: sessions.filter(s => s.status === 'waiting').length,
    completedChats: sessions.filter(s => s.status === 'closed').length,
    avgResponseTime: '2.3 min', // This would come from analytics
    satisfactionRate: '94%' // This would come from analytics
  }

  const statCards = [
    {
      name: 'Total Chats',
      value: stats.totalChats,
      icon: MessageSquare,
      color: 'bg-blue-500',
      change: '+12%',
      changeType: 'increase'
    },
    {
      name: 'Active Chats',
      value: stats.activeChats,
      icon: Activity,
      color: 'bg-green-500',
      change: '+3',
      changeType: 'increase'
    },
    {
      name: 'Waiting',
      value: stats.waitingChats,
      icon: Clock,
      color: 'bg-yellow-500',
      change: '-2',
      changeType: 'decrease'
    },
    {
      name: 'Completed',
      value: stats.completedChats,
      icon: CheckCircle,
      color: 'bg-purple-500',
      change: '+8',
      changeType: 'increase'
    }
  ]

  const recentSessions = sessions
    .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
    .slice(0, 5)

  return (
    <div className="p-6 space-y-6">
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white"
      >
        <h1 className="text-2xl font-bold mb-2">
          Welcome back, {agent?.firstName}! 👋
        </h1>
        <p className="text-primary-100">
          You have {stats.waitingChats} customers waiting and {stats.activeChats} active conversations.
        </p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="card p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <div className="flex items-center mt-2">
                  <span
                    className={`text-sm font-medium ${
                      stat.changeType === 'increase'
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}
                  >
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 ml-1">vs last week</span>
                </div>
              </div>
              <div className={`p-3 rounded-full ${stat.color}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sessions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="lg:col-span-2 card"
        >
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Sessions</h2>
          </div>
          <div className="p-6">
            {recentSessions.length > 0 ? (
              <div className="space-y-4">
                {recentSessions.map((session) => (
                  <div
                    key={session.sessionId}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-primary-600" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          User {session.userId.slice(-6)}
                        </p>
                        <p className="text-sm text-gray-500">
                          {format(new Date(session.lastActivity), 'MMM d, h:mm a')}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`badge ${
                          session.status === 'active'
                            ? 'badge-success'
                            : session.status === 'waiting'
                            ? 'badge-warning'
                            : 'badge-gray'
                        }`}
                      >
                        {session.status}
                      </span>
                      <span className="badge badge-gray">
                        {session.mode}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent sessions</p>
              </div>
            )}
          </div>
        </motion.div>

        {/* Quick Actions & Performance */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/chat')}
                className="w-full btn btn-primary text-left"
              >
                <MessageSquare className="h-4 w-4 mr-2" />
                Start New Chat
              </button>
              <button
                onClick={() => navigate('/customers')}
                className="w-full btn btn-secondary text-left"
              >
                <Users className="h-4 w-4 mr-2" />
                View All Customers
              </button>
              <button
                onClick={() => navigate('/analytics')}
                className="w-full btn btn-secondary text-left"
              >
                <Activity className="h-4 w-4 mr-2" />
                View Analytics
              </button>
            </div>
          </motion.div>

          {/* Performance Metrics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="card p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance</h3>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Avg Response Time</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.avgResponseTime}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <TrendingUp className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Satisfaction Rate</span>
                </div>
                <span className="text-sm font-medium text-green-600">{stats.satisfactionRate}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <UserCheck className="h-5 w-5 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-600">Status</span>
                </div>
                <span className="badge badge-success">Online</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default Dashboard
